/**
 * Utility functions for Intercom SDK that don't require Vue instance context
 * Single source of truth for all Intercom SDK calls
 * Safe to call outside of Vue component context
 */

import {
  update as updateIntercomSDK,
  shutdown as shutdownIntercomSDK
} from '@intercom/messenger-js-sdk';
import type { IntercomUpdateData, IntercomUserInfo } from '@/types/intercom';
import { isProduction } from '@/utils/environment';

const { VITE_INTERCOM_APP_ID, VITE_VERSION } = import.meta.env;

/**
 * Returns true when Intercom is configured for this build/environment.
 * Used to skip SDK calls on builds where VITE_INTERCOM_APP_ID is not set.
 */
export const isIntercomEnabled = (): boolean => !!VITE_INTERCOM_APP_ID;

/**
 * Update Intercom with user information or theme configuration.
 * No-ops when Intercom is not configured (e.g. preview / CloudFront builds).
 */
export const updateIntercomUserData = (data: IntercomUpdateData) => {
  if (!isIntercomEnabled()) return;
  updateIntercomSDK(data);
};

/**
 * Sync user profile with Intercom (production only).
 * Extracts email, name, roles, tenants, and other user metadata.
 * Safe to call outside Vue context (no composables required).
 *
 * On non-production environments, this is a no-op to prevent user identity tracking
 * while still allowing the widget to display with theme colors.
 */
export const syncIntercomUser = (
  userProfile: any,
  options?: { hasSingleContext?: boolean }
) => {
  if (!userProfile || !isIntercomEnabled()) return;

  // Only sync user identity in production; non-prod shows anonymous widget
  if (!isProduction) return;

  const { email, firstname, lastname, permissions, tenants } = userProfile;
  const hasSingleContext = options?.hasSingleContext ?? tenants?.length === 1;

  const intercomInfo: IntercomUserInfo = {
    email,
    name: `${firstname} ${lastname}`,
    single_context: hasSingleContext ? 'true' : 'false',
    roles: permissions?.map((p) => p.role.name).join(','),
    scope_names: permissions?.map((s) => s.scopeDetails?.name).join(','),
    tenants: tenants?.map((t) => t.name).join(','),
    scopes: permissions?.map((s) => s.scope).join(','),
    appVersion: VITE_VERSION
  };

  updateIntercomUserData(intercomInfo);
};

/**
 * Ends the Intercom user session on logout.
 * shutdown() is used over update()-with-empty-strings to avoid a 400 from Intercom.
 */
export const clearIntercomUser = () => {
  if (!isIntercomEnabled()) return;
  shutdownIntercomSDK();
};
